// Dev-only fixture for the activity log — synthesizes plausible per-user
// presence timelines straight into `ActivityLogStore`. Bypasses
// `ActivityLogService` so events land at varied historical timestamps
// without needing a real ODIN session.
//
// The fixture composes coherent timelines (joined → optionally left →
// optionally rejoined → optionally left again) so the chronological log
// reads like a real session — a user who rejoins produces several ordered
// lines — instead of random kind interleavings.
import { Injectable } from '@angular/core';
import { getAllEntities } from '@ngneat/elf-entities';
import { ActivityLogStore } from '../../state/activity-log.repository';
import { UserSettingsStore } from '../../state/user-settings.repository';
import { UserStore } from '../../state/user.repository';

const FALLBACK_NAMES = [
  'Alice',
  'Bob',
  'Charlie',
  'Diana',
  'Eve',
  'Frank',
  'Grace',
  'Heidi',
  'Ivan',
  'Judy',
  'Mallory',
  'Niaj',
  'Oscar',
  'Peggy',
  'Trent',
];

const BREAKOUT_ROOMS = ['Gryffindor', 'Slytherin', 'Ravenclaw', 'Hufflepuff'];

type PoolUser = { userId: string; name: string; avatar: string };

@Injectable({ providedIn: 'root' })
export class ActivityLogFixtureService {
  constructor(
    private _store: ActivityLogStore,
    private _users: UserStore,
    private _userSettings: UserSettingsStore
  ) {}

  // Seeds approximately `count` timelines: ~70% room-presence timelines
  // (one per distinct user) and ~30% breakout-presence timelines (one per
  // distinct user × breakout pair). Each timeline emits 1–4 events, so the
  // rendered log ends up with noticeably more lines than `count` and shows a
  // healthy mix of joins and leaves.
  addActivityLogFixture(count = 30): void {
    const roomId = this._userSettings.value.roomId || 'test-room';
    const pool = this._buildUserPool();
    if (pool.length === 0) return;

    const roomRowCount = Math.min(Math.max(1, Math.round(count * 0.7)), pool.length);
    const breakoutRowCount = Math.max(0, count - roomRowCount);

    const roomUsers = this._sample(pool, roomRowCount);
    for (const user of roomUsers) {
      this._emitTimeline(user, roomId, 'room', undefined);
    }

    const seenBreakoutKeys = new Set<string>();
    let attempts = 0;
    let emitted = 0;
    while (emitted < breakoutRowCount && attempts < breakoutRowCount * 4) {
      attempts++;
      const user = pool[Math.floor(Math.random() * pool.length)];
      const breakoutRoom = BREAKOUT_ROOMS[Math.floor(Math.random() * BREAKOUT_ROOMS.length)];
      const key = `${user.userId}|${breakoutRoom}`;
      if (seenBreakoutKeys.has(key)) continue;
      seenBreakoutKeys.add(key);
      this._emitTimeline(user, roomId, 'breakout', breakoutRoom);
      emitted++;
    }
  }

  // Wipe everything in the activity log — pairs with the dev `clear`
  // command so a fixture re-run doesn't keep stacking events.
  clear(): void {
    this._store.clear();
  }

  private _buildUserPool(): PoolUser[] {
    const realUsers: PoolUser[] = this._users.store
      .query(getAllEntities())
      .filter((u) => !u.isSelf && u.data.name)
      .map((u, idx) => ({
        userId: u.id ?? `fixture-real-${idx}`,
        name: u.data.name,
        avatar: u.data.avatar,
      }));

    if (realUsers.length > 0) return realUsers;

    return FALLBACK_NAMES.map((name, idx) => {
      const userId = `fixture-${idx}`;
      return { userId, name, avatar: '' };
    });
  }

  // Walks a user through join → (optional left) → (optional rejoin) →
  // (optional final left). `time` is monotonically increasing within the
  // last 12 hours so the aggregator's `firstJoin` and `lastLeft` derive
  // cleanly from event order.
  private _emitTimeline(
    user: PoolUser,
    roomId: string,
    scope: 'room' | 'breakout',
    breakoutRoom: string | undefined
  ): void {
    const now = Date.now();
    const earliest = now - 12 * 60 * 60 * 1000;

    let cursor = earliest + Math.floor(Math.random() * (now - earliest));
    this._add(user, roomId, scope, breakoutRoom, 'joined', new Date(cursor));

    // 65% chance to leave after the initial join.
    if (Math.random() < 0.65) {
      cursor += this._minutes(1, 45);
      if (cursor > now) return;
      this._add(user, roomId, scope, breakoutRoom, 'left', new Date(cursor));

      // 35% chance to rejoin after leaving.
      if (Math.random() < 0.35) {
        cursor += this._minutes(2, 60);
        if (cursor > now) return;
        this._add(user, roomId, scope, breakoutRoom, 'joined', new Date(cursor));

        // 50% chance to leave again.
        if (Math.random() < 0.5) {
          cursor += this._minutes(1, 45);
          if (cursor > now) return;
          this._add(user, roomId, scope, breakoutRoom, 'left', new Date(cursor));
        }
      }
    }
  }

  private _add(
    user: PoolUser,
    roomId: string,
    scope: 'room' | 'breakout',
    breakoutRoom: string | undefined,
    kind: 'joined' | 'left',
    time: Date
  ): void {
    this._store.add({
      time,
      roomId,
      userId: user.userId,
      name: user.name,
      avatar: user.avatar,
      kind,
      scope,
      breakoutRoom,
    });
  }

  private _minutes(minInclusive: number, maxInclusive: number): number {
    const range = maxInclusive - minInclusive;
    return (minInclusive + Math.random() * range) * 60 * 1000;
  }

  private _sample<T>(arr: readonly T[], n: number): T[] {
    const copy = arr.slice();
    const out: T[] = [];
    for (let i = 0; i < n && copy.length > 0; i++) {
      const idx = Math.floor(Math.random() * copy.length);
      out.push(copy.splice(idx, 1)[0] as T);
    }
    return out;
  }
}
